// frontend/src/api/client.js
import axios from 'axios';
import { getToken } from '../auth/auth';

// Determine the base URL based on environment
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Add the /api prefix to the base URL
const API_BASE_URL = `${BASE_URL}/api`;

console.log('API client using base URL:', API_BASE_URL);

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  timeout: 30000, // 30 second timeout
  withCredentials: true
});

// Add request interceptor for auth token
apiClient.interceptors.request.use(config => {
  const token = getToken();
  console.log('Request interceptor - Token exists:', !!token);
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log('Added Authorization header:', `Bearer ${token.substring(0, 10)}...`);
  } else {
    console.log('No token available for request to:', config.url);
  }
  
  // Add timestamp to prevent caching
  if (config.method === 'get') {
    config.params = {
      ...config.params,
      _t: new Date().getTime()
    };
  }
  
  return config;
}, error => {
  console.error('Request error:', error);
  return Promise.reject(error);
});

// Add response interceptor to handle token expiration and errors
apiClient.interceptors.response.use(
  response => {
    console.log('Response interceptor - Status:', response.status);
    return response;
  },
  error => {
    console.error('Response error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      data: error.response?.data
    });

    if (error.code === 'ECONNABORTED') {
      return Promise.reject({
        success: false,
        message: 'Request timed out. Please check your connection and try again.'
      });
    }
    
    if (error.response) {
      // Handle 401 Unauthorized errors
      if (error.response.status === 401) {
        console.log('401 Unauthorized error details:', {
          url: error.config.url,
          message: error.response.data?.message,
          token: getToken() ? 'Token exists' : 'No token'
        });
        
        // Check if this is a Unbounce authentication error
        const isUnbounceError = error.config.url.includes('/unbounce/') && 
          error.response.data?.message?.includes('Unbounce authentication failed');
        
        if (!isUnbounceError) {
          // Only log out for non-Unbounce 401 errors
          console.log("API received 401 Unauthorized response - logging out user");
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          // Use a custom event instead of direct window.location change
          window.dispatchEvent(new CustomEvent('auth:logout'));
        }
      }
      
      // Handle 403 Forbidden errors
      if (error.response.status === 403) {
        console.log("API received 403 Forbidden response");
        // Redirect to dashboard if not admin
        if (window.location.pathname.startsWith('/admin')) {
          window.location.href = '/dashboard';
        }
      }
      
      // Return the error response data if available
      if (error.response.data) {
        return Promise.reject(error.response.data);
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Network Error:', error.request);
      return Promise.reject({
        success: false,
        message: 'Unable to connect to the server. Please check your internet connection.'
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error:', error.message);
      return Promise.reject({
        success: false,
        message: 'An unexpected error occurred. Please try again.'
      });
    }
    return Promise.reject(error);
  }
);

export { apiClient };